import { formatMoney } from "../../Common/Helpers/FormatMoney";
import { sfUrl } from '../StorefrontPaths';
import { sizedImage } from '../StorefrontImages';
/**
 * Storefront Parts Assistant — a right-side "cowork" drawer backed by the Azure
 * OpenAI deployment (/Services/Storefront/Ai/Chat). Not a chat-bubble UI: it opens
 * with a guided welcome, asks the shopper questions, and surfaces suggestion chips
 * so the conversation feels like working alongside a knowledgeable colleague.
 * Mounted globally from the layout; opens when the "AI Assist" button is pressed.
 */
interface Msg { role: "user" | "assistant"; content: string; parts?: PartCard[]; }

interface PartCard {
    Id: number;
    Title: string;
    Slug: string;
    ImageUrl: string;
    OemNumber: string | null;
    Sku: string | null;
    Price: number | null;
    Quantity: number | null;
}

const STARTERS: string[] = [
    "Find a part for my vehicle",
    "Will this part fit my car?",
    "How do I find my part number?",
    "Shipping & delivery times",
    "Returns & warranty",
];

// Persistent quick actions shown under each assistant answer.
const FOLLOWUPS: string[] = [
    "Find another part",
    "Shipping & returns",
    "Start over",
];

// Persisted per browser tab (sessionStorage) so a reload/navigation doesn't wipe the
// conversation, but a new tab or closing the browser starts fresh.
const STORAGE_KEY = "sf-ai-messages";
// Ids of every part shown so far this conversation — sent back with each request so the
// server can deterministically exclude them from a repeat "show me more" lookup instead of
// relying on the model to compute the right page offset (it doesn't; it just gets a fresh batch).
const SHOWN_PARTS_KEY = "sf-ai-shown-parts";

class AiAssistant {
    private messages: Msg[] = [];
    private shownPartIds: number[] = [];
    private drawer: HTMLElement;
    private overlay: HTMLElement;
    private thread: HTMLElement;
    private input: HTMLTextAreaElement;
    private form: HTMLFormElement;
    private busy = false;

    constructor() {
        this.drawer = document.getElementById("sf-ai-drawer") as HTMLElement;
        this.overlay = document.getElementById("sf-ai-overlay") as HTMLElement;
        this.thread = document.getElementById("sf-ai-thread") as HTMLElement;
        this.input = document.getElementById("sf-ai-text") as HTMLTextAreaElement;
        this.form = document.getElementById("sf-ai-form") as HTMLFormElement;
        if (!this.drawer || !this.form) return;

        this.messages = this.loadMessages();
        this.shownPartIds = this.loadShownPartIds();

        document.getElementById("sf-ai-open")?.addEventListener("click", () => this.open());
        document.getElementById("sf-ai-close")?.addEventListener("click", () => this.close());
        this.overlay?.addEventListener("click", () => this.close());
        document.addEventListener("keydown", (e) => { if (e.key === "Escape") this.close(); });
        this.form.addEventListener("submit", (e) => { e.preventDefault(); this.send(this.input.value); });

        // Enter sends, Shift+Enter newlines; textarea auto-grows.
        this.input?.addEventListener("keydown", (e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.send(this.input.value); }
        });
        this.input?.addEventListener("input", () => this.autoGrow());
    }

    private open(): void {
        this.drawer.classList.add("open");
        this.overlay.classList.add("open");
        this.drawer.setAttribute("aria-hidden", "false");
        if (this.thread.childElementCount === 0) {
            if (this.messages.length > 0) this.restoreThread();
            else this.renderWelcome();
        }
        window.setTimeout(() => this.input?.focus(), 60);
    }

    private close(): void {
        this.drawer.classList.remove("open");
        this.overlay.classList.remove("open");
        this.drawer.setAttribute("aria-hidden", "true");
    }

    private autoGrow(): void {
        this.input.style.height = "auto";
        this.input.style.height = Math.min(this.input.scrollHeight, 132) + "px";
    }

    // ── Rendering ──────────────────────────────────────────

    private renderWelcome(): void {
        const card = document.createElement("div");
        card.className = "sf-ai-welcome";
        card.innerHTML = `
            <div class="sf-ai-welcome-icon"><i class="fa fa-robot"></i></div>
            <h3>Let's find your part.</h3>
            <p>Tell me your vehicle's <strong>year, make, and model</strong> and the part you need —
               or pick a starting point below and I'll take it from there.</p>
            <div class="sf-ai-chips" data-role="starters"></div>`;
        this.thread.appendChild(card);
        this.renderChips(card.querySelector('[data-role="starters"]') as HTMLElement, STARTERS);
    }

    private restoreThread(): void {
        this.messages.forEach(m => {
            this.addTurn(m.role, m.content);
            if (m.role === "assistant" && m.parts && m.parts.length) this.renderParts(m.parts, false);
        });
        const last = this.messages[this.messages.length - 1];
        if (last && last.role === "assistant") this.addFollowups();
    }

    private renderChips(host: HTMLElement, labels: string[]): void {
        labels.forEach(label => {
            const chip = document.createElement("button");
            chip.type = "button";
            chip.className = "sf-ai-chip";
            chip.textContent = label;
            chip.addEventListener("click", () => {
                if (label === "Start over") { this.reset(); return; }
                this.send(label);
            });
            host.appendChild(chip);
        });
    }

    private addTurn(role: "user" | "assistant", content: string): HTMLElement {
        const turn = document.createElement("div");
        turn.className = `sf-ai-turn ${role}`;
        if (role === "user") {
            turn.innerHTML = `<div class="sf-ai-bubble-user"></div>`;
            (turn.querySelector(".sf-ai-bubble-user") as HTMLElement).textContent = content;
        } else {
            turn.innerHTML = `
                <div class="sf-ai-turn-avatar"><i class="fa fa-robot"></i></div>
                <div class="sf-ai-turn-body">
                    <div class="sf-ai-turn-name">Parts Assistant</div>
                    <div class="sf-ai-turn-text"></div>
                </div>`;
            (turn.querySelector(".sf-ai-turn-text") as HTMLElement).textContent = content;
        }
        this.thread.appendChild(turn);
        this.scrollToEnd();
        return turn;
    }

    private scrollToEnd(): void {
        this.thread.scrollTop = this.thread.scrollHeight;
    }

    private reset(): void {
        this.messages = [];
        this.saveMessages();
        this.shownPartIds = [];
        this.saveShownPartIds();
        this.thread.innerHTML = "";
        this.renderWelcome();
        this.input.value = "";
        this.autoGrow();
        this.input.focus();
    }

    // ── Persistence (sessionStorage) ────────────────────────

    private loadMessages(): Msg[] {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private saveMessages(): void {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.messages));
        } catch {
            // Storage unavailable/full — the conversation just won't survive a reload.
        }
    }

    private loadShownPartIds(): number[] {
        try {
            const raw = sessionStorage.getItem(SHOWN_PARTS_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === "number") : [];
        } catch {
            return [];
        }
    }

    private saveShownPartIds(): void {
        try {
            sessionStorage.setItem(SHOWN_PARTS_KEY, JSON.stringify(this.shownPartIds));
        } catch {
            // Storage unavailable/full — "show me more" may occasionally repeat a part after reload.
        }
    }

    // ── Conversation ───────────────────────────────────────

    private async send(raw: string): Promise<void> {
        const text = (raw || "").trim();
        if (!text || this.busy) return;

        // Drop the welcome/starter block once the conversation begins.
        const welcome = this.thread.querySelector(".sf-ai-welcome");
        if (welcome) welcome.remove();
        // Remove any lingering follow-up chips from the previous answer.
        this.thread.querySelectorAll(".sf-ai-followups").forEach(el => el.remove());

        this.input.value = "";
        this.autoGrow();
        this.messages.push({ role: "user", content: text });
        this.saveMessages();
        const userTurn = this.addTurn("user", text);

        this.busy = true;
        const thinking = this.addThinking();

        try {
            // Server-side model binding rejects the whole request if a Messages item carries
            // an unknown property, so send a slim { role, content } projection over the wire —
            // `parts` is local-only (sessionStorage + restore rendering), the server never
            // needs it back since it only reads content for conversation context.
            const wireMessages = this.messages.map(m => ({ role: m.role, content: m.content }));
            const res = await fetch("/Services/Storefront/Ai/Chat", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Messages: wireMessages, ShownPartIds: this.shownPartIds })
            });
            if (res.status === 429) {
                // #37115 per-IP rate limit: drop the unanswered question from the history (so a
                // resend isn't duplicated in the model's context) and hand it back in the input.
                thinking.remove();
                userTurn.remove();
                this.messages.pop();
                this.saveMessages();
                if (!this.input.value) { this.input.value = text; this.autoGrow(); }
                this.addTurn("assistant", "You're sending messages a little too quickly. Please wait a few minutes, then try again.");
                return;
            }
            const data = await res.json();
            const reply = (data && (data.Reply || data.reply)) || "";
            const rawError = data && (data.Error || data.error);
            // The service answers a string; pipeline errors (400/500) are Serenity's { Code, Message }.
            const error = typeof rawError === "string" ? rawError : rawError?.Message;
            const parts = (data && (data.Parts || data.parts)) as PartCard[] | undefined;
            thinking.remove();

            if (reply) {
                this.messages.push({ role: "assistant", content: reply, parts: parts && parts.length ? parts : undefined });
                this.saveMessages();
                this.addTurn("assistant", reply);
                if (parts && parts.length) this.renderParts(parts);
                this.addFollowups();
            } else {
                this.addTurn("assistant", error || "Sorry, I couldn't answer that. Please try again.");
            }
        } catch {
            thinking.remove();
            this.addTurn("assistant", "Sorry, I'm having trouble connecting right now. Please try again in a moment.");
        } finally {
            this.busy = false;
            this.input?.focus();
        }
    }

    private addThinking(): HTMLElement {
        const turn = document.createElement("div");
        turn.className = "sf-ai-turn assistant";
        turn.innerHTML = `
            <div class="sf-ai-turn-avatar"><i class="fa fa-robot"></i></div>
            <div class="sf-ai-turn-body">
                <div class="sf-ai-turn-name">Parts Assistant</div>
                <div class="sf-ai-thinking"><span></span><span></span><span></span></div>
            </div>`;
        this.thread.appendChild(turn);
        this.scrollToEnd();
        return turn;
    }

    private addFollowups(): void {
        const row = document.createElement("div");
        row.className = "sf-ai-chips sf-ai-followups";
        this.thread.appendChild(row);
        this.renderChips(row, FOLLOWUPS);
        this.scrollToEnd();
    }

    // ── Part result cards ──────────────────────────────────

    /**
     * Renders a batch of part cards. `track` (default true) records their ids into
     * shownPartIds so future lookups exclude them; pass false when re-rendering cards that
     * were already shown and tracked earlier (e.g. restoring the thread after a reload).
     */
    private renderParts(parts: PartCard[], track = true): void {
        if (!parts || parts.length === 0) return;
        const row = document.createElement("div");
        row.className = "sf-ai-parts";
        row.innerHTML = parts.map(p => this.renderPartCard(p)).join("");
        this.thread.appendChild(row);
        this.scrollToEnd();

        if (!track) return;
        const newIds = parts.map(p => p.Id).filter(id => !this.shownPartIds.includes(id));
        if (newIds.length > 0) {
            this.shownPartIds = this.shownPartIds.concat(newIds);
            this.saveShownPartIds();
        }
    }

    private renderPartCard(p: PartCard): string {
        const href = p.Slug ? sfUrl('/product/' + encodeURIComponent(p.Slug)) : "#";
        const inStock = (p.Quantity ?? 0) > 0;
        const price = p.Price != null ? formatMoney(p.Price) : "—";
        const stockText = inStock ? "In Stock" : "Out of Stock";
        const code = p.Sku || p.OemNumber;
        return `
            <a class="sf-ai-part-card" href="${this.escapeAttr(href)}" target="_blank" rel="noopener">
                <img class="sf-ai-part-img" src="${this.escapeAttr(sizedImage(p.ImageUrl, 'thumb'))}" alt="${this.escapeAttr(p.Title)}" loading="lazy" />
                <div class="sf-ai-part-info">
                    <div class="sf-ai-part-title">${this.escape(p.Title)}</div>
                    ${code ? `<div class="sf-ai-part-code">SKU: ${this.escape(code)}</div>` : ""}
                    <div class="sf-ai-part-meta">
                        <span class="sf-ai-part-price">${price}</span>
                        <span class="sf-ai-part-stock${inStock ? "" : " out"}">${stockText}</span>
                    </div>
                </div>
                <div class="sf-ai-part-cta"><i class="fa fa-chevron-right"></i></div>
            </a>`;
    }

    private escape(s: string | null | undefined): string {
        if (s == null) return "";
        return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
    }

    private escapeAttr(s: string | null | undefined): string { return this.escape(s); }
}

function init(): void { new AiAssistant(); }
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
else init();
